import { createContext, useContext, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import YouTube from "react-youtube";
import "./ListingInfoPanel.scss";

const formatNumber = (val) => {
  if (val === null || val === undefined || val === "") return "";
  return String(val)
    .replace(/\D/g, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const localized = (item, lang) => {
  if (!item) return "";
  if (lang === "urdu") return item.nameUrdu || item.name || "";
  return item.name || "";
};

const stripColon = (s) => (typeof s === "string" ? s.replace(/[:：]\s*$/, "") : s);

const splitLines = (text) =>
  String(text)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const hasAny = (...vals) =>
  vals.some((v) => (Array.isArray(v) ? v.length > 0 : Boolean(v)));

const Row = ({ icon, label, children }) => (
  <div className='listing-info-panel__row'>
    <span className='listing-info-panel__row-label'>
      {icon ? <i className={`bx ${icon}`}></i> : null}
      {stripColon(label)}
    </span>
    <div className='listing-info-panel__row-value'>{children}</div>
  </div>
);

const ChipsRow = ({ icon, label, items, lang }) => {
  if (!items || !items.length) return null;
  return (
    <Row icon={icon} label={label}>
      <ul className='listing-info-panel__chips'>
        {items.map((a, i) => (
          <li className='listing-info-panel__chip' key={(a && a.name) || i}>
            {localized(a, lang)}
          </li>
        ))}
      </ul>
    </Row>
  );
};

// Section headings sit one level under the listing title, which is the
// page's h1 standalone and an h2 inside the modal overlays.
const SectionHeadingTag = createContext("h2");

const Section = ({ icon, title, children }) => {
  const HeadingTag = useContext(SectionHeadingTag);
  // Skip the whole section if every child collapsed to null.
  const hasContent = Array.isArray(children)
    ? children.some(Boolean)
    : Boolean(children);
  if (!hasContent) return null;
  return (
    <section className='listing-info-panel__section'>
      <HeadingTag className='listing-info-panel__section-title'>
        {icon ? <i className={`bx ${icon}`}></i> : null}
        {title}
      </HeadingTag>
      <div className='listing-info-panel__rows'>{children}</div>
    </section>
  );
};

const ytOpts = {
  width: "100%",
  playerVars: { autoplay: 0 },
};

const ListingInfoPanel = ({
  listing,
  videoId,
  moveContent,
  onToggleMoveContent,
  onClose,
  contactSlot,
  ownerCard,
}) => {
  const [t, i18n] = useTranslation("common");
  const lang = i18n.languages[0];
  const isRtl = lang === "urdu";

  const [activeTab, setActiveTab] = useState("factsAndFeatures");

  const fullBaths = listing.fullBaths ? parseFloat(listing.fullBaths) : 0;
  const threeQBaths = listing.threeQuarterBaths
    ? parseFloat(listing.threeQuarterBaths)
    : 0;
  const halfBaths = listing.halfBaths ? parseFloat(listing.halfBaths) : 0;
  const qBaths = listing.quarterBaths ? parseFloat(listing.quarterBaths) : 0;
  const totalBaths = fullBaths + threeQBaths + halfBaths + qBaths;

  const bathTooltip = useMemo(() => {
    const parts = [];
    if (listing.fullBaths)
      parts.push(`${listing.fullBaths} ${t("viewPropDetails.full_ba")}`);
    if (listing.threeQuarterBaths)
      parts.push(`${listing.threeQuarterBaths} ${t("viewPropDetails.3q_ba")}`);
    if (listing.halfBaths)
      parts.push(`${listing.halfBaths} ${t("viewPropDetails.half_ba")}`);
    if (listing.quarterBaths)
      parts.push(`${listing.quarterBaths} ${t("viewPropDetails.q_ba")}`);
    return parts.join(" + ");
  }, [
    listing.fullBaths,
    listing.threeQuarterBaths,
    listing.halfBaths,
    listing.quarterBaths,
    t,
  ]);

  const renderRentPeriod = () => {
    if (listing.status !== "Rent") return null;
    if (listing.rentDuration === "perMonth")
      return `/ ${t("viewPropDetails.month")}`;
    if (listing.rentDuration === "per6Months")
      return `/ 6 ${t("viewPropDetails.months")}`;
    if (listing.rentDuration === "perYear")
      return `/ ${t("viewPropDetails.year")}`;
    return null;
  };

  const renderAreaUnits = () => {
    const u = listing.areaUnit;
    const cells = [];
    const push = (val, key) => {
      if (val) cells.push(`${val} ${t(`viewPropDetails.${key}`)}`);
    };
    if (u === "km") {
      push(listing.areaInMarla, "marla");
      push(listing.areaInKanal, "kanal");
    } else if (u === "bkm") {
      push(listing.areaInBega, "bega");
      push(listing.areaInKanal, "kanal");
      push(listing.areaInMarla, "marla");
    } else if (u === "acre") {
      push(listing.areaInAcre, "acre");
      push(listing.areaInKanal, "kanal");
      push(listing.areaInMarla, "marla");
    } else if (u === "ma") {
      push(listing.areaInMurabba, "murabba");
      push(listing.areaInAcre, "acre");
    } else if (u === "m") {
      push(listing.areaInMarla, "marla");
    }
    return cells.length ? cells.join(" · ") : null;
  };

  const areaUnitsText = renderAreaUnits();
  const priceValue =
    listing.status === "Rent" ? listing.rentPrice : listing.price;
  const hasPrice = listing.price || listing.rentPrice;

  // Tabs to show.
  const tabs = [
    { key: "overview", label: t("viewPropDetails.overview") },
    { key: "factsAndFeatures", label: t("viewPropDetails.facts") },
  ];
  if (listing.videoUrl && videoId) {
    tabs.push({ key: "video", label: t("viewPropDetails.video") });
  }

  const TitleTag = onClose ? "h2" : "h1";

  return (
    <SectionHeadingTag.Provider value={onClose ? "h3" : "h2"}>
    <div
      className={`listing-info-panel${isRtl ? " rtl" : ""}`}
      dir={isRtl ? "rtl" : "ltr"}
    >
      {(onClose || onToggleMoveContent) && (
        <div className='listing-info-panel__top-controls'>
          {onToggleMoveContent ? (
            <button
              type='button'
              className='listing-info-panel__icon-button listing-info-panel__collapse-toggle'
              onClick={onToggleMoveContent}
              aria-label='Toggle panel size'
            >
              <i
                className={`bx ${
                  moveContent ? "bxs-chevron-down" : "bxs-chevron-up"
                }`}
              ></i>
            </button>
          ) : null}
          {onClose ? (
            <button
              type='button'
              className='listing-info-panel__icon-button listing-info-panel__close'
              onClick={onClose}
              aria-label='Close'
            >
              <i className='bx bx-x'></i>
            </button>
          ) : null}
        </div>
      )}

      <div className='listing-info-panel__header'>
        {listing.title ? (
          <TitleTag className='visually-hidden'>{listing.title}</TitleTag>
        ) : null}
        <div className='listing-info-panel__status-row'>
          {listing.status === "Sale" ? (
            <span className='listing-info-panel__status listing-info-panel__status--sale'>
              <i className='bx bx-purchase-tag-alt'></i>
              {t("viewPropDetails.sale")}
            </span>
          ) : listing.status === "Rent" ? (
            <span className='listing-info-panel__status listing-info-panel__status--rent'>
              <i className='bx bx-key'></i>
              {t("viewPropDetails.rent")}
            </span>
          ) : null}
          {listing.uid ? (
            <span className='listing-info-panel__uid'>{listing.uid}</span>
          ) : null}
        </div>

        {hasPrice ? (
          <div className='listing-info-panel__price'>
            <span className='listing-info-panel__price-currency'>
              {t("viewPropDetails.pkr")}
            </span>
            <span className='listing-info-panel__price-amount'>
              {formatNumber(priceValue)}
            </span>
            {renderRentPeriod() ? (
              <span className='listing-info-panel__price-period'>
                {renderRentPeriod()}
              </span>
            ) : null}
          </div>
        ) : null}

        {listing.status === "Rent" && listing.rentSecurityDeposit ? (
          <div className='listing-info-panel__security'>
            {stripColon(t("viewPropDetails.security"))}{" "}
            <strong>{formatNumber(listing.rentSecurityDeposit)}</strong>
          </div>
        ) : null}

        <div className='listing-info-panel__stats'>
          {listing.beds ? (
            <span className='listing-info-panel__stat'>
              <i className='bx bx-bed'></i>
              {listing.beds}
            </span>
          ) : null}
          {totalBaths ? (
            <OverlayTrigger
              placement='top'
              trigger={["hover", "focus"]}
              delay={{ show: 250 }}
              overlay={<Tooltip id='allBaths'>{bathTooltip}</Tooltip>}
            >
              <span className='listing-info-panel__stat listing-info-panel__stat--tooltipped'>
                <i className='bx bx-bath'></i>
                {totalBaths}
              </span>
            </OverlayTrigger>
          ) : null}
          {listing.areaInSqft ? (
            <span className='listing-info-panel__stat'>
              <i className='bx bx-area'></i>
              {formatNumber(listing.areaInSqft)} {t("viewPropDetails.sqft")}
            </span>
          ) : null}
          {areaUnitsText ? (
            <span className='listing-info-panel__stat'>
              <i className='bx bx-ruler'></i>
              {areaUnitsText}
            </span>
          ) : null}
        </div>

        {listing.address ? (
          <div className='listing-info-panel__address'>
            <i className='bx bxs-map'></i>
            <span>{listing.address}</span>
          </div>
        ) : null}

        {ownerCard}

        {contactSlot ? (
          <div className='listing-info-panel__actions'>{contactSlot}</div>
        ) : null}
      </div>

      <div className='listing-info-panel__tabs'>
        {tabs.map((tab) => (
          <button
            type='button'
            key={tab.key}
            className={`listing-info-panel__tab${
              activeTab === tab.key ? " listing-info-panel__tab--active" : ""
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className='listing-info-panel__tab-body'>
        {activeTab === "overview" ? (
          <>
            {(listing.area || listing.city || listing.state) && (
              <Section icon='bx-map-pin' title='Location'>
                {listing.area ? (
                  <Row icon='bx-pin' label={t("viewPropDetails.area")}>
                    {listing.area}
                  </Row>
                ) : null}
                {listing.city ? (
                  <Row icon='bx-buildings' label={t("viewPropDetails.city")}>
                    {listing.city}
                  </Row>
                ) : null}
                {listing.state ? (
                  <Row icon='bx-map' label={t("viewPropDetails.state")}>
                    {listing.state}
                  </Row>
                ) : null}
              </Section>
            )}

            {(listing.dateAvailableFrom || listing.dateAvailableTo) && (
              <Section icon='bx-calendar' title='Availability'>
                {listing.dateAvailableFrom ? (
                  <Row icon='bx-calendar' label={t("viewPropDetails.from")}>
                    {listing.dateAvailableFrom.split("-").reverse().join("-")}
                  </Row>
                ) : null}
                {listing.dateAvailableTo ? (
                  <Row icon='bx-calendar-check' label={t("viewPropDetails.to")}>
                    {listing.dateAvailableTo.split("-").reverse().join("-")}
                  </Row>
                ) : null}
              </Section>
            )}

            {listing.description ? (
              <Section icon='bx-file' title={stripColon(t("viewPropDetails.desc"))}>
                <div className='listing-info-panel__description'>
                  {listing.description}
                </div>
              </Section>
            ) : null}

            {(listing.nearbySchools ||
              listing.nearbyHospitals ||
              listing.nearbyShoppingMalls ||
              listing.nearbyRestaurants) && (
              <Section icon='bx-buildings' title='Nearby'>
                {listing.nearbySchools ? (
                  <Row icon='bxs-school' label={t("viewPropDetails.schools")}>
                    <ul className='listing-info-panel__chips'>
                      {splitLines(listing.nearbySchools).map((s) => (
                        <li className='listing-info-panel__chip' key={s}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </Row>
                ) : null}
                {listing.nearbyHospitals ? (
                  <Row icon='bx-plus-medical' label={t("viewPropDetails.hospitals")}>
                    <ul className='listing-info-panel__chips'>
                      {splitLines(listing.nearbyHospitals).map((s) => (
                        <li className='listing-info-panel__chip' key={s}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </Row>
                ) : null}
                {listing.nearbyShoppingMalls ? (
                  <Row icon='bx-store' label={t("viewPropDetails.shops")}>
                    <ul className='listing-info-panel__chips'>
                      {splitLines(listing.nearbyShoppingMalls).map((s) => (
                        <li className='listing-info-panel__chip' key={s}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </Row>
                ) : null}
                {listing.nearbyRestaurants ? (
                  <Row icon='bx-restaurant' label={t("viewPropDetails.rests")}>
                    <ul className='listing-info-panel__chips'>
                      {splitLines(listing.nearbyRestaurants).map((s) => (
                        <li className='listing-info-panel__chip' key={s}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </Row>
                ) : null}
              </Section>
            )}

            {!listing.description &&
              !listing.area &&
              !listing.dateAvailableFrom &&
              !listing.nearbySchools && (
                <div className='listing-info-panel__empty'>—</div>
              )}
          </>
        ) : null}

        {activeTab === "factsAndFeatures" ? (
          <>
            {hasAny(
              listing.propertySubType,
              listing.architecturalStyle,
              listing.hasParking === "Yes",
              listing.hasGarage === "Yes",
              listing.privatePool,
              listing.fireplace,
              listing.petsAllowed
            ) && (
            <Section icon='bx-info-circle' title={t("viewPropDetails.facts")}>
              {listing.propertySubType ? (
                <Row icon='bx-landscape' label={t("viewPropDetails.type")}>
                  {localized(listing.propertySubType, lang)}
                </Row>
              ) : null}
              {listing.architecturalStyle ? (
                <Row icon='bx-building-house' label={t("viewPropDetails.arch")}>
                  {localized(listing.architecturalStyle, lang)}
                </Row>
              ) : null}
              {listing.hasParking === "Yes" ? (
                <Row icon='bxs-parking' label={t("viewPropDetails.parking")}>
                  {t("viewPropDetails.yes")}
                  {listing.parkingSpaces && listing.parkingSpaces === 1
                    ? ` (${listing.parkingSpaces} ${t("viewPropDetails.space")})`
                    : listing.parkingSpaces && listing.parkingSpaces > 1
                    ? ` (${listing.parkingSpaces} ${t("viewPropDetails.spaces")})`
                    : ""}
                </Row>
              ) : null}
              {listing.hasGarage === "Yes" ? (
                <Row icon='bxs-car-garage' label={t("viewPropDetails.garage")}>
                  {t("viewPropDetails.yes")}
                </Row>
              ) : null}
              {listing.privatePool ? (
                <Row icon='bx-swim' label={t("viewPropDetails.pool")}>
                  {listing.privatePool === true
                    ? t("viewPropDetails.yes")
                    : t("viewPropDetails.no")}
                </Row>
              ) : null}
              {listing.fireplace ? (
                <Row icon='bx-candles' label={t("viewPropDetails.fireplace")}>
                  {listing.fireplace === true
                    ? t("viewPropDetails.yes")
                    : t("viewPropDetails.no")}
                </Row>
              ) : null}
              {listing.petsAllowed ? (
                <Row icon='bx-cat' label={t("viewPropDetails.pets")}>
                  {listing.petsAllowed === true
                    ? t("viewPropDetails.yess")
                    : t("viewPropDetails.no")}
                </Row>
              ) : null}
            </Section>
            )}

            {listing.yearBuilt ? (
              <Section icon='bx-hammer' title={t("viewPropDetails.construct")}>
                <Row icon='bx-calendar' label={t("viewPropDetails.condition")}>
                  {t("viewPropDetails.built")} {listing.yearBuilt}
                </Row>
              </Section>
            ) : null}

            {hasAny(
              listing.appliances,
              listing.basement,
              listing.floorCoverings,
              listing.indoorFeatures,
              listing.rooms
            ) && (
              <Section icon='bx-home-smile' title={t("viewPropDetails.roomDetails")}>
                <ChipsRow
                  icon='bx-fridge'
                  label={t("viewPropDetails.app")}
                  items={listing.appliances}
                  lang={lang}
                />
                {listing.basement ? (
                  <Row icon='bx-down-arrow-alt' label={t("viewPropDetails.base")}>
                    {localized(listing.basement, lang)}
                  </Row>
                ) : null}
                <ChipsRow
                  icon='bx-square'
                  label={t("viewPropDetails.floor")}
                  items={listing.floorCoverings}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-star'
                  label={t("viewPropDetails.indoor")}
                  items={listing.indoorFeatures}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-door-open'
                  label={t("viewPropDetails.rooms")}
                  items={listing.rooms}
                  lang={lang}
                />
              </Section>
            )}

            {hasAny(
              listing.coolingTypes,
              listing.heatingTypes,
              listing.heatingFuels
            ) && (
              <Section icon='bx-cog' title={t("viewPropDetails.utilityDetails")}>
                <ChipsRow
                  icon='bx-wind'
                  label={t("viewPropDetails.coolT")}
                  items={listing.coolingTypes}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-flame'
                  label={t("viewPropDetails.heatT")}
                  items={listing.heatingTypes}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-gas-pump'
                  label={t("viewPropDetails.heatF")}
                  items={listing.heatingFuels}
                  lang={lang}
                />
              </Section>
            )}

            {hasAny(
              listing.architecturalStyle,
              listing.buildingAmenities,
              listing.exteriors,
              listing.outdoorAmenities,
              listing.parkings,
              listing.roofs,
              listing.views
            ) && (
              <Section icon='bx-buildings' title={t("viewPropDetails.otherDetails")}>
                {listing.architecturalStyle ? (
                  <Row icon='bx-building-house' label={t("viewPropDetails.archiStyle")}>
                    {localized(listing.architecturalStyle, lang)}
                  </Row>
                ) : null}
                <ChipsRow
                  icon='bx-bath'
                  label={t("viewPropDetails.buildAmen")}
                  items={listing.buildingAmenities}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-cuboid'
                  label={t("viewPropDetails.exter")}
                  items={listing.exteriors}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-tree'
                  label={t("viewPropDetails.outdoor")}
                  items={listing.outdoorAmenities}
                  lang={lang}
                />
                <ChipsRow
                  icon='bxs-parking'
                  label={t("viewPropDetails.parkings")}
                  items={listing.parkings}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-building'
                  label={t("viewPropDetails.roof")}
                  items={listing.roofs}
                  lang={lang}
                />
                <ChipsRow
                  icon='bx-image-alt'
                  label={t("viewPropDetails.view")}
                  items={listing.views}
                  lang={lang}
                />
              </Section>
            )}

            {listing.additionalDetails && listing.additionalDetails.length ? (
              <Section icon='bx-plus' title={t("viewPropDetails.additional")}>
                <Row label={t("viewPropDetails.additional")}>
                  <ul className='listing-info-panel__chips'>
                    {listing.additionalDetails.map((a, i) => (
                      <li
                        className='listing-info-panel__chip'
                        key={(a && a.name) || i}
                      >
                        {localized(a, lang)}
                      </li>
                    ))}
                  </ul>
                </Row>
              </Section>
            ) : null}
          </>
        ) : null}

        {activeTab === "video" && videoId ? (
          <div className='listing-info-panel__video'>
            <YouTube videoId={videoId} opts={ytOpts} />
          </div>
        ) : null}
      </div>
    </div>
    </SectionHeadingTag.Provider>
  );
};

export default ListingInfoPanel;
